import { useEffect, useState, useMemo, useCallback } from 'react';
import defaultInstance from '../axiosHelper';
import { API_ENDPOINTS } from '../utils/apiEndPoints';

export default function useBootstrap() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    from_date: null,
    to_date: null,
    district: 'ALL'
  });

  const [districtsRaw, setDistrictsRaw] = useState([]); // [{DISTRICT}]
  const [summaryAll, setSummaryAll] = useState(null); // {count_screenings, count_villages, count_hfn, count_htn, count_dm}
  const [allDistrictCounts, setAllDistrictCounts] = useState([]); // [{DISTRICT, count_hfn, count_htn, count_dm}]
  const [ageWiseScreening, setAgeWiseScreening] = useState(null); // Age-wise screening data by district and screen type

  // Define fetchBootstrap function to be used throughout the component
  const fetchBootstrap = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await defaultInstance.get(API_ENDPOINTS.BOOTSTRAP);
      if (res?.data?.success) {
        const data = res.data.data || {};
        setDistrictsRaw(Array.isArray(data.districts) ? data.districts : []);
        setSummaryAll(data.summaryAll || null);
        setAllDistrictCounts(Array.isArray(data.allDistrictCounts) ? data.allDistrictCounts : []);
        setAgeWiseScreening(data.ageWiseScreening || null);
      } else {
        throw new Error('Bootstrap API returned unsuccessful response');
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch on component mount
  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      await fetchBootstrap();
    };

    if (active) {
      fetchData();
    }

    return () => {
      active = false;
    };
  }, [fetchBootstrap]);

  const districts = useMemo(() => {
    // Map to {label, value}
    return (districtsRaw || []).map(r => {
      const name = (r?.DISTRICT || '').toUpperCase();
      return { label: name, value: name };
    });
  }, [districtsRaw]);

  const districtIndexByName = useMemo(() => {
    const map = new Map();
    (districtsRaw || []).forEach((r, idx) => {
      const name = (r?.DISTRICT || '').toUpperCase();
      if (name) map.set(name, idx);
    });
    return map;
  }, [districtsRaw]);

  const countsByDistrict = useMemo(() => {
    const map = new Map();
    (allDistrictCounts || []).forEach(r => {
      const name = (r?.DISTRICT || '').toUpperCase();
      if (name) map.set(name, r);
    });
    return map;
  }, [allDistrictCounts]);

  // Function to fetch data with filters
  const fetchWithFilters = useCallback(async (newFilters) => {
    const { from_date, to_date } = { ...filters, ...newFilters };

    // If we have date filters, use the new endpoint
    if (from_date && to_date) {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          from_date,
          to_date
        });

        const res = await defaultInstance.get(`${API_ENDPOINTS.SCREENING_SUMMARY}?${params}`);

        if (res?.data?.success) {
          const data = res.data.data || {};
          // Update summary with the filtered data for all districts
          if (data.summaryAll) {
            setSummaryAll({
              ...data.summaryAll,
              count_screenings: data.summaryAll.count_screenings || 0,
              count_villages: data.summaryAll.count_villages || 0,
              count_hfn: data.summaryAll.count_hfn || 0,
              count_htn: data.summaryAll.count_htn || 0,
              count_dm: data.summaryAll.count_dm || 0,
              dm_with_htn: data.summaryAll.dm_with_htn || 0
            });
          }

          // Process age-wise screening data with null checks and type safety
          const processAgeWiseData = (rawData) => {
            if (!rawData || typeof rawData !== 'object') return {};
            
            const processed = {};
            
            Object.entries(rawData).forEach(([district, screenTypes]) => {
              if (!district || !screenTypes || typeof screenTypes !== 'object') return;
              
              processed[district] = processed[district] || {};
              
              Object.entries(screenTypes).forEach(([screenType, ageGroups]) => {
                if (!screenType || !ageGroups || typeof ageGroups !== 'object') return;
                
                processed[district][screenType] = {
                  '18-29': {
                    male: Number(ageGroups['18-29']?.male) || 0,
                    female: Number(ageGroups['18-29']?.female) || 0
                  },
                  '30-39': {
                    male: Number(ageGroups['30-39']?.male) || 0,
                    female: Number(ageGroups['30-39']?.female) || 0
                  },
                  '40-49': {
                    male: Number(ageGroups['40-49']?.male) || 0,
                    female: Number(ageGroups['40-49']?.female) || 0
                  },
                  '50-59': {
                    male: Number(ageGroups['50-59']?.male) || 0,
                    female: Number(ageGroups['50-59']?.female) || 0
                  },
                  '60+': {
                    male: Number(ageGroups['60+']?.male) || 0,
                    female: Number(ageGroups['60+']?.female) || 0
                  }
                };
              });
            });
            
            return processed;
          };
          
          // Set processed age-wise screening data
          setAgeWiseScreening(processAgeWiseData(data.ageWiseScreening) || {});
          
          // Update district counts if available
          if (data.allDistrictCounts && Array.isArray(data.allDistrictCounts)) {
            // Create a map of districts that have data for the filtered date range
            const districtsWithData = new Map();
            data.allDistrictCounts
              .filter(district => district.DISTRICT !== 'ALL DISTRICTS')
              .forEach(district => {
                districtsWithData.set(district.DISTRICT, district);
              });

            // Ensure all districts are included, even if they have 0 counts
            const allDistricts = districtsRaw || [];
            const formattedDistricts = allDistricts.map(district => {
              const districtName = district.DISTRICT;
              const hasData = districtsWithData.has(districtName);

              if (hasData) {
                const districtData = districtsWithData.get(districtName);
                return {
                  DISTRICT: districtData.DISTRICT || '',
                  count_screenings: districtData.count_screenings || 0,
                  count_villages: districtData.count_villages || 0,
                  count_hfn: districtData.count_hfn || 0,
                  count_htn: districtData.count_htn || 0,
                  count_dm: districtData.count_dm || 0,
                  dm_with_htn: districtData.dm_with_htn || 0,
                  count_cvd: districtData.count_cvd || 0,
                  count_ckd: districtData.count_ckd || 0,
                  count_copd: districtData.count_copd || 0,
                  count_mental: districtData.count_mental || 0,
                  count_stroke: districtData.count_stroke || 0,
                  count_BMI: districtData.count_BMI || 0,
                  OTHER: districtData.OTHER || 0,
                  // Female count fields for gender-wise charts
                  count_htn_sex_f: districtData.count_htn_sex_f || 0,
                  count_dm_sex_f: districtData.count_dm_sex_f || 0,
                  count_dm_with_htn_sex_f: districtData.count_dm_with_htn_sex_f || 0,
                  count_cvd_sex_f: districtData.count_cvd_sex_f || 0,
                  count_ckd_sex_f: districtData.count_ckd_sex_f || 0,
                  count_copd_sex_f: districtData.count_copd_sex_f || 0,
                  count_mental_sex_f: districtData.count_mental_sex_f || 0,
                  count_stroke_sex_f: districtData.count_stroke_sex_f || 0,
                  count_bmi_sex_f: districtData.count_bmi_sex_f || 0,
                  // Cancer-related fields
                  sex_f_oral_cancer: districtData.sex_f_oral_cancer || 0,
                  sex_m_oral_cancer: districtData.sex_m_oral_cancer || 0,
                  breast_cancer: districtData.breast_cancer || 0,
                  cervical_cancer: districtData.cervical_cancer || 0,
                  // Gender-wise screening data
                  count_sex_f: districtData.count_sex_f || 0,
                  // Age group data
                  htn_18_29: districtData.htn_18_29 || 0,
                  dm_18_29: districtData.dm_18_29 || 0,
                  htn_30_39: districtData.htn_30_39 || 0,
                  dm_30_39: districtData.dm_30_39 || 0,
                  htn_40_49: districtData.htn_40_49 || 0,
                  dm_40_49: districtData.dm_40_49 || 0,
                  htn_50_59: districtData.htn_50_59 || 0,
                  dm_50_59: districtData.dm_50_59 || 0,
                  htn_60: districtData.htn_60 || 0,
                  dm_60: districtData.dm_60 || 0,
                  // Gender-wise age group data
                  sex_f_18_29: districtData.sex_f_18_29 || 0,
                  sex_m_18_29: districtData.sex_m_18_29 || 0,
                  sex_f_30_39: districtData.sex_f_30_39 || 0,
                  sex_m_30_39: districtData.sex_m_30_39 || 0,
                  sex_f_40_49: districtData.sex_f_40_49 || 0,
                  sex_m_40_49: districtData.sex_m_40_49 || 0,
                  sex_f_50_59: districtData.sex_f_50_59 || 0,
                  sex_m_50_59: districtData.sex_m_50_59 || 0,
                  sex_f_60: districtData.sex_f_60 || 0,
                  sex_m_60: districtData.sex_m_60 || 0,
                  // Age Wise Screening Data
                  ageWiseScreening: districtData.ageWiseScreening || 0,
                };
              } else {
                // District has no data for this date range, return with 0 counts
                return {
                  DISTRICT: districtName || '',
                  count_screenings: 0,
                  count_villages: 0,
                  count_hfn: 0,
                  count_htn: 0,
                  count_dm: 0,
                  dm_with_htn: 0,
                  count_cvd: 0,
                  count_ckd: 0,
                  count_copd: 0,
                  count_mental: 0,
                  count_stroke: 0,
                  count_BMI: 0,
                  OTHER: 0,
                  // Female count fields for gender-wise charts
                  count_htn_sex_f: 0,
                  count_dm_sex_f: 0,
                  count_dm_with_htn_sex_f: 0,
                  count_cvd_sex_f: 0,
                  count_ckd_sex_f: 0,
                  count_copd_sex_f: 0,
                  count_mental_sex_f: 0,
                  count_stroke_sex_f: 0,
                  count_bmi_sex_f: 0,
                  // Cancer-related fields
                  sex_f_oral_cancer: 0,
                  sex_m_oral_cancer: 0,
                  breast_cancer: 0,
                  cervical_cancer: 0,
                  // Gender-wise screening data
                  count_sex_f: 0,
                  // Age group data
                  htn_18_29: 0,
                  dm_18_29: 0,
                  htn_30_39: 0,
                  dm_30_39: 0,
                  htn_40_49: 0,
                  dm_40_49: 0,
                  htn_50_59: 0,
                  dm_50_59: 0,
                  htn_60: 0,
                  dm_60: 0,
                  // Gender-wise age group data
                  sex_f_18_29: 0,
                  sex_m_18_29: 0,
                  sex_f_30_39: 0,
                  sex_m_30_39: 0,
                  sex_f_40_49: 0,
                  sex_m_40_49: 0,
                  sex_f_50_59: 0,
                  sex_m_50_59: 0,
                  sex_f_60: 0,
                  sex_m_60: 0,
                  // Age Wise Screening Data
                  ageWiseScreening: 0,
                };
              }
            });
            setAllDistrictCounts(formattedDistricts);
          } else {
            setAllDistrictCounts([]);
          }
        }
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    } else {
      // If no date filters, use the original bootstrap endpoint
      fetchBootstrap();
    }

    // Update filters
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, [filters, fetchBootstrap, districtsRaw]);

  return {
    loading,
    error,
    districts,
    districtIndexByName,
    countsByDistrict,
    summaryAll,
    allDistrictCounts,
    ageWiseScreening,
    fetchWithFilters,
    filters,
    setFilters,
    refetch: fetchBootstrap,
  };
}
